import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, Link2, Trash2, Trophy, TrendingUp, Newspaper, Search, X, RefreshCw, Zap, RotateCw, BarChart3, Share2 } from "lucide-react";
import { toast } from "sonner";
import ConsensusPill from "@/components/ConsensusPill";
import DynastyWire from "@/components/DynastyWire";
import MatrixRain from "@/components/MatrixRain";

export default function Dashboard() {
  const { user, refresh, access } = useAuth();
  const [syncedLeagues, setSyncedLeagues] = useState([]);
  const [teaser, setTeaser] = useState(null);
  const [sleeperUsername, setSleeperUsername] = useState("");
  const [availableLeagues, setAvailableLeagues] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [prevAccounts, setPrevAccounts] = useState([]);
  const [connecting, setConnecting] = useState(false);
  const [refreshingFmt, setRefreshingFmt] = useState(false);
  const nav = useNavigate();

  const loadSynced = async () => {
    const r = await api.get("/leagues");
    setSyncedLeagues(r.data.leagues || []);
  };

  const loadAccounts = async () => {
    const r = await api.get("/sleeper/accounts");
    setAccounts(r.data.accounts || []);
  };

  const loadPrevAccounts = async () => {
    const r = await api.get("/sleeper/previous-accounts");
    setPrevAccounts(r.data.accounts || []);
  };

  const loadAvailable = async () => {
    const r = await api.get("/sleeper/leagues");
    setAvailableLeagues(r.data.leagues || []);
  };

  useEffect(() => { loadSynced(); loadAccounts(); loadPrevAccounts(); loadAvailable(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    if (syncedLeagues.length > 0 && !access?.has_access) {
      api.get("/teasers").then(r => setTeaser(r.data)).catch(() => {});
    }
  }, [syncedLeagues.length, access?.has_access]);

  const doConnect = async (username) => {
    if (!username || !username.trim()) return;
    setConnecting(true);
    try {
      const r = await api.post("/sleeper/connect", { username: username.trim() });
      setSleeperUsername("");
      await Promise.all([loadAccounts(), loadPrevAccounts(), loadAvailable()]);
      await refresh();
      toast.success(`Linked Sleeper account @${r.data.sleeper_user.username}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sleeper user not found");
    } finally {
      setConnecting(false);
    }
  };

  const connect = async (e) => {
    e.preventDefault();
    await doConnect(sleeperUsername);
  };

  const removeAccount = async (sid, name) => {
    await api.delete(`/sleeper/accounts/${sid}`);
    await Promise.all([loadAccounts(), loadPrevAccounts(), loadAvailable()]);
    await refresh();
    toast.success(`Unlinked @${name}`);
  };

  const refreshFormats = async () => {
    setRefreshingFmt(true);
    try {
      const r = await api.post("/leagues/refresh-formats", {});
      await loadSynced();
      toast.success(`Refreshed formats for ${r.data.updated} league${r.data.updated !== 1 ? "s" : ""}`);
    } catch { toast.error("Could not refresh formats"); }
    finally { setRefreshingFmt(false); }
  };

  const syncOne = async (league_id, name) => {
    try {
      await api.post("/leagues/sync", { league_id });
      toast.success(`Synced ${name} — your league is ranked`, {
        action: { label: "Share rankings", onClick: () => shareLeague(league_id) },
      });
      loadSynced();
    } catch { toast.error("Sync failed"); }
  };

  const shareLeague = async (league_id) => {
    try {
      const r = await api.post(`/leagues/${league_id}/power-rankings/share`);
      await navigator.clipboard.writeText(r.data.url);
      toast.success("Public rankings link copied — paste it in your league chat");
    } catch { toast.error("Couldn't create a share link. Try the Share button on Power Rankings."); }
  };

  const unsync = async (league_id) => {
    await api.delete(`/leagues/${league_id}`);
    loadSynced();
  };

  return (
    <div className="relative min-h-[80vh]">
      <MatrixRain columns={26} style={{ opacity: 0.16, position: "fixed", top: "3.5rem", left: "16rem", right: 0, bottom: 0, height: "auto" }} />
      <div className="relative z-10 space-y-8 fade-up">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[var(--accent)] text-xs tracking-widest mb-1">// COMMAND CENTER</div>
          <h1 className="font-display text-4xl font-bold" data-testid="dashboard-heading">Welcome, {user?.name}.</h1>
          <p className="text-[var(--text-2)] mt-2">Connect your Sleeper account and sync your dynasty leagues to unlock the terminal.</p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/app/values"
            data-testid="dashboard-dme-values-cta"
            className="btn-primary inline-flex items-center gap-2 whitespace-nowrap"
            title="Browse every player's DME value"
          >
            <BarChart3 className="w-4 h-4" />
            DME Player Values
          </Link>
          <ConsensusPill />
        </div>
      </header>

      {/* Trial value recap (#3) */}
      {access?.on_trial && (access?.days_left ?? 0) >= 0 && (
        <section className="card-bordered p-4 border-l-2 border-l-[var(--accent)] flex flex-wrap items-center justify-between gap-3" data-testid="trial-recap-banner">
          <div className="text-sm text-[var(--text-2)]">
            <span className="text-[var(--accent)] font-semibold">{access.days_left} day{access.days_left !== 1 ? "s" : ""} left</span> in your Pro trial.
            {syncedLeagues.length > 0 && <span> You're tracking <span className="text-white font-medium">{syncedLeagues.length} league{syncedLeagues.length !== 1 ? "s" : ""}</span>{teaser?.moves ? <> and Dynasty Metrics has <span className="text-white font-medium">{teaser.moves} live move{teaser.moves !== 1 ? "s" : ""}</span> queued for you</> : null}.</span>}
            {" "}Keep your edge — stay Pro.
          </div>
          <button onClick={() => nav("/pricing")} className="btn-secondary text-xs shrink-0" data-testid="trial-recap-cta">Manage plan</button>
        </section>
      )}

      {/* Free-user value teaser (#2) */}
      {!access?.has_access && !access?.on_trial && teaser && teaser.moves > 0 && (
        <section className="card-bordered neon-border p-5 relative overflow-hidden" data-testid="teaser-banner">
          <div className="text-[10px] tracking-widest text-[var(--accent)] font-mono mb-2">// PRO INSIGHT · LOCKED</div>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="min-w-0">
              <h2 className="font-display text-xl font-bold">
                Dynasty Metrics found <span className="text-[var(--accent)]">{teaser.moves} high-leverage move{teaser.moves !== 1 ? "s" : ""}</span> across your {teaser.leagues_scanned} league{teaser.leagues_scanned !== 1 ? "s" : ""}.
              </h2>
              <p className="text-sm text-[var(--text-2)] mt-1">
                {teaser.sell_high > 0 ? <>{teaser.sell_high} sell-high window{teaser.sell_high !== 1 ? "s" : ""} and buy-low targets are waiting in your Command Center. </> : null}
                Start your free trial to see exactly who to trade, and how.
              </p>
            </div>
            <button onClick={() => nav("/app/command")} className="btn-primary text-sm inline-flex items-center gap-2 shrink-0" data-testid="teaser-cta">
              <Zap className="w-4 h-4" /> Unlock the moves
            </button>
          </div>
        </section>
      )}

      {accounts.length === 0 && (
        <section className="card-bordered neon-border p-6 terminal-grid relative overflow-hidden" data-testid="onboarding-banner">
          <div className="relative">
            <div className="text-[10px] tracking-widest text-[var(--accent)] font-mono mb-2">// GET STARTED IN 60 SECONDS</div>
            <h2 className="font-display text-2xl font-bold mb-2">Connect Sleeper to unlock the terminal.</h2>
            <p className="text-sm text-[var(--text-2)] max-w-xl mb-4">Link your Sleeper username below to sync your dynasty leagues. The moment you do, you'll get 10,000-sim pick futures, your portfolio dashboard, the draft war room, and the trade heat map — all live.</p>
            <div className="flex flex-wrap gap-2 text-[11px] font-mono text-[var(--text-3)]">
              {["1 · Enter your Sleeper username", "2 · Pick the leagues to sync", "3 · Start trading like a quant"].map(s => (
                <span key={s} className="border border-[var(--border)] rounded-sm px-2 py-1">{s}</span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Sleeper Sync */}
      <section className="card-bordered p-6" data-testid="sleeper-sync-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-display text-lg font-semibold">Sleeper Accounts</div>
            <div className="text-xs text-[var(--text-3)] tracking-wider">
              {accounts.length > 0 ? `${accounts.length} ACCOUNT${accounts.length !== 1 ? "S" : ""} LINKED` : "NOT CONNECTED"}
            </div>
          </div>
          <Link2 className="w-5 h-5 text-[var(--accent)]" />
        </div>

        {accounts.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4" data-testid="linked-accounts">
            {accounts.map(a => (
              <div key={a.sleeper_user_id} className="inline-flex items-center gap-2 border border-[var(--border)] bg-black/40 rounded-sm px-3 py-1.5 text-sm" data-testid={`account-${a.sleeper_user_id}`}>
                <span className="text-[var(--accent)]">@{a.sleeper_username}</span>
                <button onClick={() => removeAccount(a.sleeper_user_id, a.sleeper_username)} className="text-[var(--text-3)] hover:text-red-400" data-testid={`unlink-${a.sleeper_user_id}`} title="Unlink">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        <form onSubmit={connect} className="flex gap-2">
          <input
            data-testid="sleeper-username-input"
            className="input-dark flex-1"
            placeholder={accounts.length ? "Link another Sleeper username…" : "Your Sleeper username"}
            value={sleeperUsername}
            onChange={(e) => setSleeperUsername(e.target.value)}
          />
          <button data-testid="sleeper-connect-btn" disabled={connecting} className="btn-primary inline-flex items-center gap-2 whitespace-nowrap">
            {connecting ? <Loader2 className="w-4 h-4 spin" /> : <Search className="w-4 h-4" />}
            {accounts.length ? "Add Account" : "Fetch Leagues"}
          </button>
        </form>
        <p className="text-xs text-[var(--text-3)] mt-2" data-testid="sync-helper">
          Just enter <span className="text-[var(--text-2)]">one Sleeper username</span> — we automatically pull in <span className="text-[var(--accent)]">all of your dynasty leagues</span>. Redraft leagues are skipped to keep your terminal focused.
        </p>

        {prevAccounts.length > 0 && (
          <div className="mt-4" data-testid="prev-accounts">
            <div className="text-[10px] tracking-widest text-[var(--text-3)] mb-2 font-mono">PREVIOUSLY SYNCED — ONE-CLICK RE-LINK</div>
            <div className="flex flex-wrap gap-2">
              {prevAccounts.map(a => (
                <button
                  key={a.sleeper_user_id}
                  disabled={connecting}
                  onClick={() => doConnect(a.sleeper_username)}
                  data-testid={`relink-${a.sleeper_user_id}`}
                  className="inline-flex items-center gap-1.5 border border-[var(--border)] bg-black/40 rounded-sm px-3 py-1.5 text-sm text-[var(--text-2)] hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors disabled:opacity-50"
                  title={`Re-link @${a.sleeper_username}`}
                >
                  <RotateCw className="w-3.5 h-3.5" />
                  @{a.sleeper_username}
                </button>
              ))}
            </div>
          </div>
        )}

        {availableLeagues.length > 0 && (
          <div className="mt-6">
            <div className="text-xs tracking-widest text-[var(--text-3)] mb-3">AVAILABLE LEAGUES ({availableLeagues.length})</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {availableLeagues.map(l => {
                const synced = syncedLeagues.some(s => s.league_id === l.league_id);
                return (
                  <div key={l.league_id} className="flex items-center justify-between border border-[var(--border)] rounded-sm p-3 hover:bg-white/[0.02]" data-testid={`league-row-${l.league_id}`}>
                    <div className="min-w-0">
                      <div className="text-sm truncate">{l.name}</div>
                      <div className="text-[10px] text-[var(--text-3)] tracking-wider">
                        {l.total_rosters} TEAMS · {l.season}{l.sleeper_username ? ` · @${l.sleeper_username}` : ""}
                      </div>
                    </div>
                    <button onClick={() => syncOne(l.league_id, l.name)} disabled={synced}
                      className={`text-xs ${synced ? "text-[var(--text-3)]" : "text-[var(--accent)] hover:underline"}`}
                      data-testid={`sync-btn-${l.league_id}`}>
                      {synced ? "Synced" : "Sync"}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {/* Synced leagues quick actions */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs tracking-widest text-[var(--text-3)]">YOUR SYNCED LEAGUES</div>
          {syncedLeagues.length > 0 && (
            <button onClick={refreshFormats} disabled={refreshingFmt} className="text-xs text-[var(--text-2)] hover:text-[var(--accent)] inline-flex items-center gap-1.5" data-testid="refresh-formats-btn">
              <RefreshCw className={`w-3.5 h-3.5 ${refreshingFmt ? "spin" : ""}`} /> Refresh formats
            </button>
          )}
        </div>
        {syncedLeagues.length === 0 ? (
          <div className="card-bordered p-8 text-center text-[var(--text-2)]" data-testid="empty-synced">
            No leagues synced yet. Enter your Sleeper username above to get started.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {syncedLeagues.map(l => (
              <div key={l.league_id} className="card-bordered p-5 group hover:border-zinc-700 transition-colors" data-testid={`synced-${l.league_id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="font-display font-semibold">{l.name}</div>
                    <div className="text-[10px] tracking-widest text-[var(--text-3)] mt-1">
                      {l.total_rosters} TEAMS · {l.season}
                      {l.format && <span className="ml-1 text-[var(--accent)]">· {l.format.toUpperCase()}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => shareLeague(l.league_id)} className="opacity-0 group-hover:opacity-100 text-[var(--text-3)] hover:text-[var(--accent)] transition" title="Copy public rankings link" data-testid={`share-${l.league_id}`}>
                      <Share2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => unsync(l.league_id)} className="opacity-0 group-hover:opacity-100 text-[var(--text-3)] hover:text-red-400 transition" title="Remove" data-testid={`unsync-${l.league_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="space-y-2 mt-4">
                  <button onClick={() => nav(`/app/power-rankings?league=${l.league_id}`)} className="btn-secondary w-full text-sm inline-flex items-center justify-center gap-2" data-testid={`go-power-${l.league_id}`}>
                    <Trophy className="w-3.5 h-3.5" /> Power Rankings
                  </button>
                  <button onClick={() => nav(`/app/trade-finder?league=${l.league_id}`)} className="btn-secondary w-full text-sm" data-testid={`go-tradefinder-${l.league_id}`}>
                    Trade Finder
                  </button>
                  <button onClick={() => nav(`/app/news?league=${l.league_id}`)} className="btn-secondary w-full text-sm inline-flex items-center justify-center gap-2" data-testid={`go-news-${l.league_id}`}>
                    <Newspaper className="w-3.5 h-3.5" /> Squad News
                  </button>
                  <button onClick={() => nav(`/app/leagues/${l.league_id}/context`)} className="w-full text-sm inline-flex items-center justify-center gap-2 rounded-sm py-2 bg-[var(--accent)]/10 border border-[var(--accent)]/30 text-[var(--accent)] hover:bg-[var(--accent)]/20 transition-colors" data-testid={`go-context-${l.league_id}`}>
                    <Zap className="w-3.5 h-3.5" /> DME League Intelligence
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <button onClick={() => nav("/app/market")} className="card-bordered p-6 text-left hover:border-zinc-700" data-testid="quick-market">
          <TrendingUp className="w-5 h-5 text-[var(--accent)] mb-3" />
          <div className="font-display font-semibold">Market Trends</div>
          <div className="text-xs text-[var(--text-2)] mt-1">Risers & fallers across 7d/30d.</div>
        </button>
        <button onClick={() => nav("/app/trade-calculator")} className="card-bordered p-6 text-left hover:border-zinc-700" data-testid="quick-calc">
          <Trophy className="w-5 h-5 text-[var(--accent)] mb-3" />
          <div className="font-display font-semibold">Trade Calculator</div>
          <div className="text-xs text-[var(--text-2)] mt-1">Multi-asset balance check.</div>
        </button>
        <button onClick={() => nav("/app/news")} className="card-bordered p-6 text-left hover:border-zinc-700" data-testid="quick-news">
          <Newspaper className="w-5 h-5 text-[var(--accent)] mb-3" />
          <div className="font-display font-semibold">Global News</div>
          <div className="text-xs text-[var(--text-2)] mt-1">Live NFL injury & headlines.</div>
        </button>
      </section>

      <DynastyWire />
      </div>
    </div>
  );
}
